// v11
// Path: frontend/src/components/DataCanvasV3/CanvasDispatcher.js
// Inputs: Action payloads from drag-and-drop or UI interactions, existing canvasConfig
// Outputs: Pure, standardized canvasConfig containing ONLY levels: { [instanceId]: { syntaxPath, columns, subgrids, displayStyle } }
// Summary: Immutable dispatcher for Data Canvas V3 state management. Completely eliminates vestigial rootGrids arrays, fileItems sections, canvas__ prefixes, and subgrid filterConditions objects. Full Syntax Train strings serve as the single source of truth for grid identity and join logic.

function makeInstanceId() {
    return `grid_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
}

function parseMultiHopPaths(fullSyntaxPath) {
    if (!fullSyntaxPath) return [fullSyntaxPath];

    if (fullSyntaxPath.includes('{FS}') || fullSyntaxPath.includes('FILE_SYSTEM::')) {
        return [fullSyntaxPath];
    }

    let prefixTag = '';
    let restPath = fullSyntaxPath;

    if (restPath.startsWith('[')) {
        const cb = restPath.indexOf(']');
        if (cb > 0) {
            prefixTag = restPath.substring(0, cb + 1);
            restPath = restPath.substring(cb + 1);
        }
    }

    if (!restPath.includes('->')) {
        return [fullSyntaxPath];
    }

    const segments = restPath.split('->');
    const progressivePaths = [];
    let currPath = prefixTag;

    segments.forEach((seg, idx) => {
        const cleanSeg = seg.trim();
        if (!cleanSeg) return;

        // Filter out initial database container hop (e.g. "SapphireOps" without a dot schema)
        if (idx === 0 && !cleanSeg.includes('.') && segments.length > 1) {
            currPath += cleanSeg;
            return;
        }

        if (currPath && currPath !== prefixTag && !currPath.endsWith('->')) {
            currPath += `->${cleanSeg}`;
        } else {
            currPath += cleanSeg;
        }

        progressivePaths.push(currPath);
    });

    return progressivePaths.length > 0 ? progressivePaths : [fullSyntaxPath];
}

const CanvasDispatcher = {
    dispatch(payload, config) {
        if (!payload || !payload.type) return config;

        const safeConfig = config || { levels: {} };

        switch (payload.type) {
            case 'ADD_ROOT_TABLE':
                return CanvasDispatcher._addRootTable(payload, safeConfig);
            case 'ADD_ROOT_FS':
                return CanvasDispatcher._addRootFs(payload, safeConfig);
            case 'ADD_COLUMN':
                return CanvasDispatcher._addColumn(payload, safeConfig);
            case 'ADD_SUBGRID':
                return CanvasDispatcher._addSubgrid(payload, safeConfig);
            case 'REMOVE_GRID':
                return CanvasDispatcher._removeGrid(payload, safeConfig);
            case 'UPDATE_COLUMNS':
                return CanvasDispatcher._updateColumns(payload, safeConfig);
            default:
                console.warn(`[CanvasDispatcher] Unknown payload type: ${payload.type}`);
                return safeConfig;
        }
    },

    _addRootTable(payload, config) {
        const safeConfig = config || {};
        const { dropZoneId = 'canvas', connectionPrefix, schema = 'dbo', table, path } = payload;
        if (!table && !path) return safeConfig;

        const syntaxPath = path || (connectionPrefix
            ? `${connectionPrefix}->${schema}.${table}`
            : `${schema}.${table}`);

        return CanvasDispatcher._insertGrid(syntaxPath, dropZoneId, [], safeConfig);
    },

    _addRootFs(payload, config) {
        const safeConfig = config || {};
        const { dropZoneId = 'canvas', connectionPrefix, path } = payload;
        const syntaxPath = connectionPrefix ? `${connectionPrefix}{FS}${path}` : `{FS}${path}`;
        return CanvasDispatcher._insertGrid(
            syntaxPath, dropZoneId,
            ['Name', 'Type', 'Path'].map(c => `${syntaxPath}.${c}`),
            safeConfig
        );
    },

    _addColumn(payload, config) {
        const safeConfig = config || {};
        const { dropZoneId = 'canvas', connectionPrefix, schema = 'dbo', table, columnKey, columnName, path } = payload;
        
        let targetColKey = columnKey;
        let syntaxPath = path;
        
        if (!syntaxPath && targetColKey && targetColKey.includes('.')) {
            syntaxPath = targetColKey.substring(0, targetColKey.lastIndexOf('.'));
        }
        if (!syntaxPath) {
            syntaxPath = connectionPrefix
                ? `${connectionPrefix}->${schema}.${table}`
                : `${schema}.${table}`;
        }
        if (!targetColKey) {
            targetColKey = columnName
                ? `${syntaxPath}.${columnName}`
                : `${syntaxPath}.ID`;
        }

        return CanvasDispatcher._insertGrid(syntaxPath, dropZoneId, [targetColKey], safeConfig);
    },

    _addSubgrid(payload, config) {
        const safeConfig = config || {};
        const { parentInstanceId, syntaxPath } = payload;
        if (!parentInstanceId || !syntaxPath) return safeConfig;

        const subInstanceId = makeInstanceId();
        const next = { ...safeConfig, levels: { ...(safeConfig.levels || {}) } };
        const parent = { ...(next.levels[parentInstanceId] || { columns: [], subgrids: [] }) };

        const subgrids = (parent.subgrids || []).map(sg => typeof sg === 'string' ? sg : (sg.instanceId || sg.id)).filter(Boolean);
        if (!subgrids.includes(subInstanceId)) {
            subgrids.push(subInstanceId);
        }
        parent.subgrids = subgrids;
        next.levels[parentInstanceId] = parent;

        if (!next.levels[subInstanceId]) {
            next.levels[subInstanceId] = {
                syntaxPath,
                columns: [],
                subgrids: [],
                displayStyle: 'default'
            };
        }

        return next;
    },

    _removeGrid(payload, config) {
        const safeConfig = config || {};
        const { instanceId } = payload;
        if (!instanceId) return safeConfig;

        const next = { ...safeConfig, levels: { ...(safeConfig.levels || {}) } };
        delete next.levels[instanceId];

        Object.keys(next.levels).forEach(lid => {
            const level = next.levels[lid];
            if (level && level.subgrids) {
                level.subgrids = level.subgrids.filter(sg => {
                    const sgId = typeof sg === 'string' ? sg : (sg.instanceId || sg.id);
                    return sgId !== instanceId;
                });
            }
        });

        // Clean up legacy rootGrids if present
        delete next.rootGrids;
        delete next.fileItems;

        return next;
    },

    _updateColumns(payload, config) {
        const safeConfig = config || {};
        const { instanceId, columns } = payload;
        if (!instanceId || !Array.isArray(columns)) return safeConfig;

        const next = { ...safeConfig, levels: { ...(safeConfig.levels || {}) } };
        const level = { ...(next.levels[instanceId] || { subgrids: [] }) };
        level.columns = columns;
        next.levels[instanceId] = level;
        return next;
    },

    _insertGrid(syntaxPath, dropZoneId, initialColumns, config) {
        const safeConfig = config || {};

        if (dropZoneId && dropZoneId !== 'canvas' && safeConfig.levels && safeConfig.levels[dropZoneId]) {
            // Append columns to existing targeted grid
            const next = { ...safeConfig, levels: { ...(safeConfig.levels || {}) } };
            const targetLevel = { ...next.levels[dropZoneId] };
            const existing = targetLevel.columns || [];
            const merged = [...existing];
            (initialColumns || []).forEach(c => {
                if (!merged.includes(c)) merged.push(c);
            });
            targetLevel.columns = merged;
            next.levels[dropZoneId] = targetLevel;
            return next;
        }

        // Multi-hop cascading grid & subgrid creation
        const progressivePaths = parseMultiHopPaths(syntaxPath);
        const next = { ...safeConfig, levels: { ...(safeConfig.levels || {}) } };

        const rootPath = progressivePaths[0];
        
        // Find existing level matching rootPath or create new clean level
        let parentInstanceId = Object.keys(next.levels).find(k => next.levels[k]?.syntaxPath === rootPath);
        if (!parentInstanceId) {
            parentInstanceId = makeInstanceId();
            next.levels[parentInstanceId] = {
                syntaxPath: rootPath,
                columns: progressivePaths.length === 1 ? (initialColumns || []) : [],
                subgrids: [],
                displayStyle: 'default'
            };
        } else if (progressivePaths.length === 1 && initialColumns && initialColumns.length > 0) {
            const existingCols = next.levels[parentInstanceId].columns || [];
            const merged = [...existingCols];
            initialColumns.forEach(c => { if (!merged.includes(c)) merged.push(c); });
            next.levels[parentInstanceId].columns = merged;
        }

        // Construct intermediate subgrids if syntaxPath contains multi-hop relationships
        for (let i = 1; i < progressivePaths.length; i++) {
            const subPath = progressivePaths[i];
            
            let subInstanceId = Object.keys(next.levels).find(k => next.levels[k]?.syntaxPath === subPath);
            if (!subInstanceId) {
                subInstanceId = makeInstanceId();
            }

            const parentLevel = { ...(next.levels[parentInstanceId] || { columns: [], subgrids: [] }) };
            const subgridList = (parentLevel.subgrids || []).map(sg => typeof sg === 'string' ? sg : (sg.instanceId || sg.id)).filter(Boolean);
            
            if (!subgridList.includes(subInstanceId)) {
                subgridList.push(subInstanceId);
            }
            parentLevel.subgrids = subgridList;
            next.levels[parentInstanceId] = parentLevel;

            const isDeepest = (i === progressivePaths.length - 1);
            const existingSub = next.levels[subInstanceId] || { syntaxPath: subPath, columns: [], subgrids: [], displayStyle: 'default' };
            const subColumns = isDeepest ? (initialColumns || []) : [];
            const mergedSubCols = [...(existingSub.columns || [])];
            subColumns.forEach(c => {
                if (!mergedSubCols.includes(c)) mergedSubCols.push(c);
            });

            next.levels[subInstanceId] = {
                ...existingSub,
                syntaxPath: subPath,
                columns: mergedSubCols
            };

            parentInstanceId = subInstanceId;
        }

        // Clean up vestigial top-level keys
        delete next.rootGrids;
        delete next.fileItems;

        return next;
    }
};

export default CanvasDispatcher;
